import {useState, useCallback} from 'react';

export interface TableRowData {
  [columnKey: string]: string;
}

export interface SelectedRowsData {
  [rowIndex: number]: TableRowData;
}

export interface UseDifferentialDiagnosisTableReturn {
  selectedRows: Set<number>;
  selectedRowsData: SelectedRowsData;
  toggleRowSelection: (rowIndex: number, rowData: TableRowData) => void;
  clearSelection: () => void;
  getSelectedRowsReadable: () => string[];
}

export const useDifferentialDiagnosisTable =
  (): UseDifferentialDiagnosisTableReturn => {
    const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
    const [selectedRowsData, setSelectedRowsData] = useState<SelectedRowsData>(
      {},
    );

    const toggleRowSelection = useCallback(
      (rowIndex: number, rowData: TableRowData) => {
        setSelectedRows(prev => {
          const newSet = new Set(prev);
          const wasSelected = newSet.has(rowIndex);
          
          if (wasSelected) {
            newSet.delete(rowIndex);
          } else {
            newSet.add(rowIndex);
          }
          
          // Update selectedRowsData based on the new selection state
          setSelectedRowsData(prevData => {
            if (wasSelected) {
              // Remove from data if deselecting
              const newData = {...prevData};
              delete newData[rowIndex];
              return newData;
            } else {
              // Add to data if selecting
              return {
                ...prevData,
                [rowIndex]: rowData,
              };
            }
          });
          
          return newSet;
        });
      },
      [],
    );

    const clearSelection = useCallback(() => {
      setSelectedRows(new Set());
      setSelectedRowsData({});
    }, []);

    const getSelectedRowsReadable = useCallback((): string[] => {
      return Object.values(selectedRowsData).map(rowData => {
        return Object.entries(rowData)
          .map(([columnHeader, value]) => `${columnHeader}: ${value}`)
          .join(', ');
      });
    }, [selectedRowsData]);

    return {
      selectedRows,
      selectedRowsData,
      toggleRowSelection,
      clearSelection,
      getSelectedRowsReadable,
    };
  };
